import { Video } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { EnhancePromptButton } from "@/components/flowly/EnhancePromptButton/EnhancePromptButton";
import { QuickSuggestions } from "@/components/flowly/QuickSuggestions/QuickSuggestions";

interface VideoPromptAreaProps {
  modelDisplayName: string;
  promptPlaceholder: string;
  showFeaturedSuggestion: boolean;
  prompt: string;
  onPromptChange: (value: string) => void;
  onEnhancePrompt: () => void;
  isEnhancing: boolean;
}

export const VideoPromptArea = ({
  modelDisplayName,
  promptPlaceholder,
  showFeaturedSuggestion,
  prompt,
  onPromptChange,
  onEnhancePrompt,
  isEnhancing,
}: VideoPromptAreaProps) => {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-foreground font-bold text-base md:text-lg flex items-center gap-2">
        <Video className="w-5 h-5 text-brand" />
        {t.generate.animationInstructions}
        <span className="text-xs text-muted-foreground font-normal">
          ({t.generate.model} {modelDisplayName})
        </span>
      </h3>

      <div className="flex flex-col gap-4">
        {/* Prompt Input */}
        <div className="rounded-3xl border border-brand/20 bg-card p-6 shadow-[0_0_30px_hsl(var(--brand)/0.06)] hover:shadow-[0_0_40px_hsl(var(--brand)/0.1)] hover:border-brand/30 transition-all duration-500">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-semibold text-muted-foreground/80 uppercase tracking-wider">
              {t.generate.describeMotion}
            </label>
            <EnhancePromptButton
              onEnhance={onEnhancePrompt}
              isEnhancing={isEnhancing}
              disabled={!prompt.trim()}
            />
          </div>
          <textarea
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder={promptPlaceholder}
            className="w-full h-32 bg-secondary/30 border border-border/80 rounded-2xl p-4 text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:border-brand/50 focus:bg-secondary/50 focus:shadow-[0_0_20px_hsl(var(--brand)/0.08),inset_0_0_20px_hsl(var(--brand)/0.03)] hover:border-border hover:bg-secondary/40 transition-all duration-300"
          />
        </div>

        {/* Quick Suggestions */}
        <QuickSuggestions
          suggestions={[
            t.generate.rotate360,
            t.generate.slowZoomIn,
            t.generate.dynamicBackground,
            t.generate.sparklingParticles,
            t.generate.waveEffect,
            t.generate.cinematicMotion,
          ]}
          onSelect={onPromptChange}
          featured={showFeaturedSuggestion ? {
            label: t.generate.animateBetweenImages,
            badge: t.generate.recommended,
          } : undefined}
        />

      </div>
    </div>
  );
};
